import { yupResolver } from "@hookform/resolvers/yup";
import * as Accordion from "@radix-ui/react-accordion";
import { Box, Callout, Card, Flex, Spinner, Tabs, Text } from "@radix-ui/themes";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { resetStudentData, setStudentData } from "../../redux/slices/school/student";
import { resetUserData, setUserData } from "../../redux/slices/users/user";
import { RootState } from "../../redux/store";
import { getStudentByCode } from "../../services/school/student/getStudentByCode";
import { getMyStudentAssessments, getStudentsAssessments } from "../../services/school/student/getStudentsAssessments";
import { getUserById } from "../../services/user/getUsers";
import { GroupedByYearType, StudentAssessmentType } from "../../types/Assessment";
import { GetStudentDataFormValidation } from "../Subscriptions/GetStudentDataFormValidation";
import { UserPreview } from "../Users/UserPreview";
import { PagedResultsType } from "../../types/PagedResults";

type ActiveSegmentTab = 'user' | 'departament';

export const FindStudentGrades = () => {
    const dispatch = useAppDispatch();
    const studentData = useSelector((state: RootState) => state.student.student);
    const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>('user');

    const defaultValues = {
        studentCode: '',
        id:''
    };
    const { control, handleSubmit, setValue, getValues, formState: { errors }, watch, reset } = useForm<{studentCode: string, id?:string}>({
        resolver: yupResolver(GetStudentDataFormValidation),
        defaultValues,
        mode: 'onBlur',
    });
    const { isLoading: isLoadingGetStudent, refetch: fetchStudentByCode, isError: isErrorGetStudent, error } = useQuery(
        ['getStudentByCode'],
        async () => {
            const studentCodeValue = getValues('studentCode');

            // if (studentCodeValue.trim().length < 17) return;
            try {
                const studentData = await getStudentByCode(studentCodeValue);
                if (!studentData) return;
                setValue('id', studentData.id);
                const userData = await getUserById(studentData.idUser)
                if (!userData) return;
                dispatch(setStudentData(studentData));
                dispatch(setUserData(userData));
            } catch (error) {
                dispatch(resetStudentData());
                dispatch(resetUserData());
            }
        },
        {
            enabled: !!getValues('studentCode'),
            refetchOnWindowFocus: false,
            retry: true
        }
    );
    const handleStudentCodeChange = useCallback(
        debounce((value: string) => {
            const trimmedValue = value.trim();
            setValue('studentCode', trimmedValue);
            if (trimmedValue.length >= 3) {
                fetchStudentByCode();
            } else {
                dispatch(resetStudentData());
                dispatch(resetUserData());
            }
            refreshStudentAssessments()

        }, 300),
        [setValue, fetchStudentByCode]
    );
    const onSubmit = handleSubmit(async (data) => {

    })
    const navigateAndValidate = (tabToNavigateTo: ActiveSegmentTab) => {
        if (activeSegment !== tabToNavigateTo) setActiveSegment(tabToNavigateTo);
    }

    const { isLoading: isLoadingStudentAssessments, data: studentAssessments, isError: isErrorStudentAssessments, refetch: refreshStudentAssessments } = useQuery(
        ['fetchStudentAssessments'],
        async () => {
            try {
                const studentsAssessmentsResponse = await getStudentsAssessments({ PageNumber: 1, PageSize: 400, IdStudent: getValues('id') });

                const _studentsAssessments = studentsAssessmentsResponse?.items.filter(sa => sa.idStudent === getValues('id'));
                studentsAssessmentsResponse.items = _studentsAssessments;
                return studentsAssessmentsResponse;

            } catch (error) {
                console.error(error);
            }
        },
        {
            enabled: false,
            refetchOnWindowFocus: true,
            retry: true
        }
    );

    const groupedByYear = studentAssessments?.items.reduce((acc: GroupedByYearType, assessment: StudentAssessmentType) => {
        const year = ` ${assessment.level}º Ano `;
        const semester = `${assessment.semester}º Semestre`;
        const subject = assessment.subject || "Disciplina desconhecida";
        const type = assessment.assessment || "Avaliação sem descrição";

        if (!acc[year]) {
            acc[year] = {};
        }
        if (!acc[year][semester]) {
            acc[year][semester] = {};
        }
        if (!acc[year][semester][subject]) {
            acc[year][semester][subject] = {};
        }
        if (!acc[year][semester][subject][type]) {
            acc[year][semester][subject][type] = [];
        }

        acc[year][semester][subject][type].push(assessment);
        return acc;
    }, {});

    return (
        <Card size='5'>
            <Flex direction='column' gap='6'>
                <form onSubmit={onSubmit} noValidate>
                    <TextFieldComponent
                        name="studentCode"
                        label="Código do estudante"
                        control={control}
                        errors={errors}
                        fieldProps={{
                            placeholder: 'Digite o código do estudante',
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleStudentCodeChange(e.target.value),
                            onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                                e.preventDefault();
                                const pastedText = e.clipboardData.getData('text');
                                handleStudentCodeChange(pastedText);
                            }
                        }}
                    />
                </form>
                {isLoadingGetStudent ? <Spinner loading /> :
                    isErrorGetStudent ? <Flex direction="column">
                        <Callout.Root color="red" role="alert">
                            <Text>
                                Náo foi possivel localizar o estudante
                            </Text>
                        </Callout.Root>
                    </Flex> :
                        <Card size='5'>
                            <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate) => {
                                navigateAndValidate(tabToNavigate as ActiveSegmentTab);
                            }}>
                                <Tabs.List>
                                    <Tabs.Trigger value='user' >
                                        Dados gerais
                                    </Tabs.Trigger>
                                    <Tabs.Trigger value='departament'>
                                        Notas
                                    </Tabs.Trigger>
                                </Tabs.List>
                                <Box pt="3">
                                    <Tabs.Content value="user">
                                        <UserPreview />
                                    </Tabs.Content>
                                    <Tabs.Content value="departament">
                                        <Flex direction='column' gap='4'>
                                            <Text weight="bold">Departamento:</Text>
                                            {studentData?.departament ? (
                                                <Text>{studentData?.departament}</Text>
                                            ) : (
                                                <Text color="gray">Nenhum departamento encontrado</Text>
                                            )}

                                            <Text weight="bold">Curso:</Text>
                                            {studentData?.course ? (
                                                <Text>{studentData?.course}</Text>
                                            ) : (
                                                <Text color="gray">Nenhum curso encontrado</Text>
                                            )}
                                            <Card>
                                                <Text size="5" weight="bold" style={{ marginBottom: "4pt" }}>Notas</Text>
                                                <Accordion.Root type="multiple">
                                                    {groupedByYear &&
                                                        Object.keys(groupedByYear).map((year) => (
                                                            <Accordion.Item key={year} value={year}>
                                                                <Accordion.Header>
                                                                    <Accordion.Trigger>{year}</Accordion.Trigger>
                                                                </Accordion.Header>
                                                                <Accordion.Content>
                                                                    <Accordion.Root type="multiple">
                                                                        {Object.keys(groupedByYear[year]).map((semester) => (
                                                                            <Accordion.Item key={semester} value={semester}>
                                                                                <Accordion.Header>
                                                                                    <Accordion.Trigger>{semester}</Accordion.Trigger>
                                                                                </Accordion.Header>
                                                                                <Accordion.Content>
                                                                                    {Object.keys(groupedByYear[year][semester]).map((subject, index) => (
                                                                                        <Card key={subject}>
                                                                                            <Flex key={`${subject}_${index}`} gap='5' style={{ overflowX: 'scroll' }} >
                                                                                                <Text size="4" weight="medium">{subject}</Text>
                                                                                                {Object.keys(groupedByYear[year][semester][subject]).map((type) => (
                                                                                                    <Flex key={type} gap='3' direction='column'>
                                                                                                        <Text size="3">{type}</Text>
                                                                                                        {groupedByYear[year][semester][subject][type].map((assessment) => (
                                                                                                            <Text key={assessment.id}>{assessment.grade}</Text>
                                                                                                        ))}
                                                                                                    </Flex>
                                                                                                ))}

                                                                                            </Flex>
                                                                                        </Card>
                                                                                    ))}
                                                                                </Accordion.Content>
                                                                            </Accordion.Item>
                                                                        ))}
                                                                    </Accordion.Root>
                                                                </Accordion.Content>
                                                            </Accordion.Item>
                                                        ))}
                                                </Accordion.Root>
                                            </Card>

                                        </Flex>
                                    </Tabs.Content>
                                </Box>
                            </Tabs.Root>
                        </Card>
                }

            </Flex>
        </Card>
    )
}
